import { RuleId } from '@wix/a11y-rules';
import { Box } from '@wix/design-system';
import { useTranslation } from '@wix/fe-essentials/react';
import { GroupExpansionStyle, GroupType, IssueBadge, RuleListStatus, ToggleType } from '@wix/rules-panel-ui';
import { useSetAtom } from 'jotai';
import React, { useCallback, useRef } from 'react';
import { currentTaskInfoAtom } from '../../atoms/bi-atoms';
import { GroupLoadingSkeleton } from '../../components/wizard-content/group-loading-skeleton';
import { RuleResultsSkeleton } from '../../components/wizard-content/rule-results-skeleton';
import { VerifyPageButton } from '../../components/wizard-content/verify-page-button';
import { SITE_LEVEL_PAGE_ID } from '../../consts';
import { usePanelStateAtom } from '../../hooks/rules-state';
import { useBiLogger } from '../../hooks/use-bi-logger';
import { resolveGroupAnalysisState } from '../../utils/resolve-group-analysis-state';
import { useVerify } from '../shared/use-verify';
import { useDomOrderRule } from '../site-level-rules/dom-order/use-dom-order-rule';
import { useSiteLanguageRule } from '../site-level-rules/use-site-language-rule';
import { useSkipToMainContentRule } from '../site-level-rules/use-skip-to-main-content-rule';
import { useVisualIndicatorsRule } from '../site-level-rules/use-visual-indicators-rule';
export const useBuildSiteLevelRuleGroupV2 = () => {
  const isAnalyzingRef = useRef();
  const {
    t
  } = useTranslation();
  const biLogger = useBiLogger();
  const {
    getDomOrderProps
  } = useDomOrderRule();
  const {
    getSiteLanguageProps
  } = useSiteLanguageRule();
  const {
    getSkipToMainContentProps
  } = useSkipToMainContentRule();
  const {
    getVisualIndicatorsProps
  } = useVisualIndicatorsRule();
  const {
    getGroupData
  } = usePanelStateAtom();
  const setCurrentTaskInfo = useSetAtom(currentTaskInfoAtom);
  const {
    verify
  } = useVerify({});
  const buildSiteLevelRuleGroupV2 = useCallback(props => {
    const {
      onOpen
    } = props;
    const groupData = getGroupData(SITE_LEVEL_PAGE_ID);
    if (!groupData) {
      throw new Error(`Group data not found for pageId: ${SITE_LEVEL_PAGE_ID}.`);
    }
    const {
      analysisState,
      ruleExecutionResults,
      rulesStateMap
    } = groupData;
    const orderLookup = new Map([RuleId.VISUAL_INDICATORS, RuleId.SKIP_TO_MAIN_CONTENT, RuleId.SITE_LANGUAGE, RuleId.DOM_ORDER].map((ruleId, index) => [ruleId, index]));
    const ruleResults = (ruleExecutionResults == null ? void 0 : ruleExecutionResults.filter(ruleExecutionResult => !DISABLED_SITE_RULES.includes(ruleExecutionResult.ruleId)).map(ruleExecutionResult => {
      const {
        ruleId
      } = ruleExecutionResult;
      switch (ruleId) {
        case RuleId.VISUAL_INDICATORS:
          return getVisualIndicatorsProps({
            ...props,
            ruleExecutionResult: ruleExecutionResult
          });
        case RuleId.SKIP_TO_MAIN_CONTENT:
          return getSkipToMainContentProps({
            ...props,
            ruleExecutionResult: ruleExecutionResult
          });
        case RuleId.SITE_LANGUAGE:
          return getSiteLanguageProps({
            ...props,
            ruleExecutionResult: ruleExecutionResult
          });
        case RuleId.DOM_ORDER:
          return getDomOrderProps({
            ...props,
            ruleExecutionResult: ruleExecutionResult
          });
        default:
          return null;
      }
    })) ?? [];
    const sortedRuleResults = (ruleResults ?? []).filter(ruleResult => ruleResult !== null).sort((a, b) => {
      const indexX = orderLookup.has(a.id) ? orderLookup.get(a.id) : Infinity;
      const indexY = orderLookup.has(b.id) ? orderLookup.get(b.id) : Infinity;
      return indexX - indexY;
    });
    const numberOfIssues = Array.from(rulesStateMap.values()).filter(ruleStatus => {
      var _ruleStatus$state;
      return (ruleStatus == null || (_ruleStatus$state = ruleStatus.state) == null ? void 0 : _ruleStatus$state.status) === RuleListStatus.failed;
    }).length;
    const handleVerifyClick = () => {
      return verify(groupData);
    };
    const {
      isFirstAnalysis,
      isAnalyzeFailed,
      isPendingResults,
      isAnalyzing: isAnalyzingState
    } = resolveGroupAnalysisState(analysisState);
    const isAnalyzing = isAnalyzingState || isFirstAnalysis;
    if (isAnalyzingRef.current && !isAnalyzing) {
      var _groupData$analysisDa;
      biLogger.pageScanFinished({
        analysisJobId: (groupData == null || (_groupData$analysisDa = groupData.analysisData) == null ? void 0 : _groupData$analysisDa.analysisJobId) ?? '',
        issuesCount: numberOfIssues ?? -1,
        pageId: SITE_LEVEL_PAGE_ID,
        pageType: groupData.itemType ?? SITE_LEVEL_PAGE_ID
      });
    }
    isAnalyzingRef.current = isAnalyzing;
    return {
      disabled: isFirstAnalysis || isAnalyzeFailed,
      expansionMode: GroupExpansionStyle.collapsible,
      groupId: SITE_LEVEL_PAGE_ID,
      groupType: GroupType.SiteLevel,
      isLoading: isPendingResults || isAnalyzing,
      onGroupToggle: function (toggleType, groupIndex, initiatedByUser) {
        if (initiatedByUser === void 0) {
          initiatedByUser = false;
        }
        setCurrentTaskInfo(taskInfo => ({
          ...taskInfo,
          page_id: SITE_LEVEL_PAGE_ID,
          page_index: groupIndex,
          page_type: GroupType.SiteLevel
        }));
        biLogger.pageExpandCollapse({
          initiatedByUser,
          pageId: SITE_LEVEL_PAGE_ID,
          pageIndex: groupIndex,
          pageType: GroupType.SiteLevel,
          ruleResults: sortedRuleResults,
          toggleType
        });
        toggleType === ToggleType.Expand && (onOpen == null ? void 0 : onOpen());
      },
      ruleGroupContent: {
        badge: isAnalyzeFailed ? /*#__PURE__*/React.createElement(IssueBadge, {
          label: t('accessibility-wizard.rule-group.issue-count-badge.label.scan-failed')
        }) : numberOfIssues !== 0 && !isAnalyzing ? /*#__PURE__*/React.createElement(IssueBadge, {
          label: t('accessibility-wizard.rule-group.issue-count-badge.label', {
            numberOfIssues
          })
        }) : undefined,
        badgeTooltipContent: isAnalyzeFailed ? t('accessibility-wizard.rule-group.issue-count-badge.tooltip-content.scan-failed') : undefined,
        collapsedNode: isFirstAnalysis ? /*#__PURE__*/React.createElement(GroupLoadingSkeleton, null) : isAnalyzeFailed || isAnalyzing ? /*#__PURE__*/React.createElement(Box, {
          padding: "0 3px"
        }, /*#__PURE__*/React.createElement(VerifyPageButton, {
          failedTaskCount: numberOfIssues,
          isLoading: isAnalyzing,
          pageId: SITE_LEVEL_PAGE_ID,
          taskCount: ruleResults.length,
          onClick: handleVerifyClick
        })) : undefined,
        expandNode: /*#__PURE__*/React.createElement(VerifyPageButton, {
          failedTaskCount: numberOfIssues,
          isLoading: isAnalyzing,
          pageId: SITE_LEVEL_PAGE_ID,
          taskCount: ruleResults.length,
          onClick: handleVerifyClick
        }),
        groupLabel: t('accessibility-wizard.rule-group.label.site-level')
      },
      ruleResults,
      ruleResultsSkeletonComponent: /*#__PURE__*/React.createElement(RuleResultsSkeleton, {
        analysisState: analysisState,
        pageId: SITE_LEVEL_PAGE_ID
      })
    };
  }, [biLogger, getDomOrderProps, getGroupData, getSiteLanguageProps, getSkipToMainContentProps, getVisualIndicatorsProps, setCurrentTaskInfo, verify, t]);
  return {
    buildSiteLevelRuleGroupV2
  };
};
const DISABLED_SITE_RULES = [];
//# sourceMappingURL=use-build-site-level-rule-group-v2.js.map